









import { Component, Mixins, Prop } from "vue-property-decorator";
import { ModelEditableMixin } from "../system/mixins";
import { Point, Size, Rectangle, ScreenDirection, Color, PaintStyle } from "../../domain/drawing";
import { IValueRange } from "../../domain/system";
import { DecimalUtility } from "../../utilities";

@Component
export default class BarInput extends Mixins(ModelEditableMixin) {
  @Prop({ default: () => ({ min: 0, max: 100}) })
  range: IValueRange;

  @Prop({ default: 1 })
  stepCount: number;

  @Prop({ default: "top"})
  direction: ScreenDirection;

  @Prop({ default: () => new PaintStyle(new Color("#FF0000"))})
  paintStyle: PaintStyle;

  @Prop({ default: 1 })
  scale: number;

  @Prop({ default: 0 })
  knobSize: number;

  get valueAsNumber(): number {
    return this.toValidValue(this.editingSource as number);
  }

  set valueAsNumber(source: number) {
    const current = this.toValidValue(this.editingSource as number);
    const updated = this.toValidValue(source);
    if (current !== updated) {
      this.editingSource = updated;
    }
  }

  private toValidValue(source: number): number {
    if (isNaN(source)) {
      return this.range.min;
    }
    // 計算結果に浮動小数点誤差が含まれてしまうため、それを除去するようにしている
    const result = DecimalUtility.roundUnacceptableError(Math.round(source / this.unit) * this.unit);
    return (result < this.range.min)
      ? this.range.min
      : ((this.range.max < result) ? this.range.max : result);
  }

  get unit(): number {
    return this.rangeLength / this.stepCount;
  }

  get rate(): number {
    return this.valueAsNumber / this.rangeLength;
  }

  get rangeLength(): number {
    return this.toLength(this.range);
  }

  private toLength(source: IValueRange): number {
    return source.max - source.min;
  }

  get barStyle() {
    return {
      backgroundColor: this.paintStyle.color.value,
      border: "1px solid " + this.paintStyle.color.value,
      boxSizing: "border-box",
      top: this.rectangleAsPercentage.topLeft.y + "%",
      left: this.rectangleAsPercentage.topLeft.x + "%",
      width: this.rectangleAsPercentage.size.width + "%",
      height: this.rectangleAsPercentage.size.height + "%",
    };
  }

  get knobContainerStyle() {
    const point = !!this.knobGeometryCalculators[this.direction]
      ? this.knobGeometryCalculators[this.direction](this.rateAsPercentage)
      : new Point(0, 0);
    
    return {
      left: point.x + "%",
      top: point.y + "%",
    };
  }

  get knobStyle() {
    if (!this.knobSize) {
      return {
        display: "none",
      };
    }
    const enlargeOffset = Math.floor(this.knobSize / 3);
    const enlargedSize = this.knobSize + (enlargeOffset * 2);
    const harfSize = enlargedSize / -2;
    const centeringOffset = -1 * enlargeOffset;
    return {
      top: (this.isVertical ? harfSize : centeringOffset) + "px",
      left: (!this.isVertical ? harfSize : centeringOffset) + "px",
      width: enlargedSize + "px",
      height: enlargedSize + "px",
    };
  }

  get isVertical(): boolean {
    return this.direction === "top" || this.direction === "bottom";
  }

  get rectangleAsPercentage(): Rectangle {
    return !!this.geometryCalculators[this.direction]
      ? this.geometryCalculators[this.direction](this.rateAsPercentage)
      : new Rectangle(new Point(0, 0), new Size(100, 100));
  }

  get rateAsPercentage(): number {
    return this.rate * 100;
  }

  private get geometryCalculators(): { [ key: string ]: (r: number) => Rectangle } {
    return {
      top: r => new Rectangle(new Point(0, 100 - r), new Size(100, r)),
      bottom: r => new Rectangle(new Point(0, 0), new Size(100, r)),
      left: r => new Rectangle(new Point(100 - r, 0), new Size(r, 100)),
      right: r => new Rectangle(new Point(0, 0), new Size(r, 100)),
    };
  }

  private get knobGeometryCalculators(): { [ key: string ]: (r: number) => Point } {
    return {
      top: r => new Point(0, 100 - r),
      bottom: r => new Point(0, r),
      left: r => new Point(100 - r, 0),
      right: r => new Point(r, 0),
    };
  }

  onPointerDowned(event: PointerEvent): void {
    if (this.disabled) { return; }
    window.addEventListener("pointermove", this.onPointerMoved, true);
    window.addEventListener("pointerup", this.onPointerUpped, true);
    this.handlePointer(event);
  }

  onPointerMoved(event: PointerEvent): void {
    this.handlePointer(event);
  }

  onPointerUpped(event: PointerEvent): void {
    window.removeEventListener("pointermove", this.onPointerMoved, true);
    window.removeEventListener("pointerup", this.onPointerUpped, true);
    this.handlePointer(event);
  }

  private handlePointer(event: PointerEvent): void {
    const clientRect = this.$el.getBoundingClientRect();
    if (!!this.valueCalculators[this.direction]) {
      this.valueAsNumber = this.valueCalculators[this.direction](new Size(this.$el.clientWidth, this.$el.clientHeight), clientRect, event, this.rangeLength);
    }
    event.stopPropagation();
  }

  private get valueCalculators(): { [ key: string ]: (s: Size, cr: DOMRect | ClientRect, e: PointerEvent, rl: number) => number } {
    return {
      top: (s, cr, e, rl) => ((s.height - ((e.clientY - cr.top) / this.scale)) / s.height) * rl,
      bottom: (s, cr, e, rl) => (((e.clientY - cr.top) / this.scale) / s.height) * rl,
      left: (s, cr, e, rl) => ((s.width - ((e.clientX - cr.left) / this.scale)) / s.width) * rl,
      right: (s, cr, e, rl) => (((e.clientX - cr.left) / this.scale) / s.width) * rl,
    };
  }
}
